import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  SlideOver,
  THead,
  TableShell,
  Td,
  Th,
  Tr,
  useToast,
} from '@ambernord/ui';
import { CheckCircle2, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { formatDateTime, formatNumber } from '../../lib/format.js';
import type { PackingDto } from './types.js';

interface LineDraft {
  packageType: string;
  count: string;
  netKg: string;
  grossKg: string;
}

const EMPTY_LINE: LineDraft = { packageType: '', count: '', netKg: '', grossKg: '' };

/**
 * Iepakojuma panelis: kopsavilkums (skaits/veids/svari/vadītājs/auto + parakstu
 * laikspiedoli) + rindu tabula. Rediģēšana (platforma/piegādātājs) SlideOver formā —
 * PUT /:id/packing (rindas AIZSTĀJ pilnu kopu; kopsavilkums atvasinās serverī).
 */
export function PackingPanel({
  supplyOrderId,
  packing,
  canEdit,
  onChanged,
}: {
  supplyOrderId: string;
  packing: PackingDto | null;
  canEdit: boolean;
  onChanged: () => void | Promise<void>;
}) {
  const { t, i18n } = useTranslation('sodetail');
  const { toast } = useToast();
  const lang = i18n.language;

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [driverName, setDriverName] = useState('');
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([]);

  const shipment = packing?.shipment ?? null;
  const packages = packing?.packages ?? [];

  function openEditor() {
    setDriverName(shipment?.driverName ?? '');
    setVehiclePlate(shipment?.vehiclePlate ?? '');
    setLines(
      packages.length > 0
        ? packages.map((p) => ({
            packageType: p.packageType ?? '',
            count: p.count != null ? String(p.count) : '',
            netKg: p.netKg ?? '',
            grossKg: p.grossKg ?? '',
          }))
        : [{ ...EMPTY_LINE }],
    );
    setOpen(true);
  }

  function setLine(idx: number, patch: Partial<LineDraft>) {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }

  async function save() {
    const packagesBody = lines
      .map((l) => {
        const entry: { packageType?: string; count?: number; netKg?: number; grossKg?: number } =
          {};
        if (l.packageType.trim()) entry.packageType = l.packageType.trim();
        const count = Number(l.count);
        if (l.count.trim() && Number.isInteger(count) && count > 0) entry.count = count;
        const net = Number(l.netKg);
        if (l.netKg.trim() && Number.isFinite(net) && net >= 0) entry.netKg = net;
        const gross = Number(l.grossKg);
        if (l.grossKg.trim() && Number.isFinite(gross) && gross >= 0) entry.grossKg = gross;
        return entry;
      })
      .filter((e) => Object.keys(e).length > 0);

    const body: Record<string, unknown> = { packages: packagesBody };
    if (driverName.trim()) body.driverName = driverName.trim();
    if (vehiclePlate.trim()) body.vehiclePlate = vehiclePlate.trim();

    setBusy(true);
    try {
      const res = await api.put<{ shipment?: unknown; error?: string }>(
        `/api/supply-orders/${supplyOrderId}/packing`,
        body,
      );
      if (res.ok) {
        toast(t('packing.saved'));
        setOpen(false);
        await onChanged();
      } else {
        toast(res.data.error ?? t('packing.error'), 'error');
      }
    } finally {
      setBusy(false);
    }
  }

  const summary: Array<[string, string]> = [
    [
      t('packing.packageCount'),
      shipment?.packageCount != null ? String(shipment.packageCount) : '—',
    ],
    [t('packing.packageType'), shipment?.packageType ?? '—'],
    [t('packing.netWeight'), formatNumber(shipment?.netWeightKg, lang)],
    [t('packing.grossWeight'), formatNumber(shipment?.grossWeightKg, lang)],
    [t('packing.driver'), shipment?.driverName ?? '—'],
    [t('packing.vehicle'), shipment?.vehiclePlate ?? '—'],
  ];

  return (
    <Card>
      <CardHeader
        title={t('packing.title')}
        action={
          canEdit ? (
            <Button size="sm" variant="secondary" onClick={openEditor}>
              {t('packing.edit')}
            </Button>
          ) : undefined
        }
      />
      <CardBody>
        {!shipment && packages.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('packing.empty')}</p>
        ) : (
          <>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
              {summary.map(([label, value]) => (
                <div key={label}>
                  <dt className="text-xs text-slate-500 dark:text-slate-400">{label}</dt>
                  <dd className="text-sm font-medium text-slate-900 dark:text-white">{value}</dd>
                </div>
              ))}
            </dl>

            {(shipment?.driverSignedAt || shipment?.receiverSignedAt) && (
              <div className="mt-3 flex flex-wrap gap-2">
                {shipment?.driverSignedAt ? (
                  <Badge tone="green">
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                    {t('packing.driverSigned', {
                      at: formatDateTime(shipment.driverSignedAt, lang),
                    })}
                  </Badge>
                ) : null}
                {shipment?.receiverSignedAt ? (
                  <Badge tone="green">
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                    {t('packing.receiverSigned', {
                      at: formatDateTime(shipment.receiverSignedAt, lang),
                    })}
                  </Badge>
                ) : null}
              </div>
            )}

            {packages.length > 0 && (
              <div className="mt-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {t('packing.lines.title')}
                </p>
                <TableShell>
                  <THead>
                    <tr>
                      <Th>{t('packing.lines.type')}</Th>
                      <Th>{t('packing.lines.count')}</Th>
                      <Th>{t('packing.lines.net')}</Th>
                      <Th>{t('packing.lines.gross')}</Th>
                    </tr>
                  </THead>
                  <tbody>
                    {packages.map((p) => (
                      <Tr key={p.id}>
                        <Td>{p.packageType ?? '—'}</Td>
                        <Td>{p.count != null ? formatNumber(p.count, lang) : '—'}</Td>
                        <Td>{formatNumber(p.netKg, lang)}</Td>
                        <Td>{formatNumber(p.grossKg, lang)}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </TableShell>
              </div>
            )}
          </>
        )}
      </CardBody>

      <SlideOver open={open} onClose={() => setOpen(false)} title={t('packing.editor.title')}>
        <div className="flex flex-1 flex-col gap-4">
          <Field label={t('packing.driver')} htmlFor="packing-driver">
            <Input
              id="packing-driver"
              value={driverName}
              onChange={(e) => setDriverName(e.target.value)}
            />
          </Field>
          <Field label={t('packing.vehicle')} htmlFor="packing-vehicle">
            <Input
              id="packing-vehicle"
              value={vehiclePlate}
              onChange={(e) => setVehiclePlate(e.target.value)}
            />
          </Field>

          <div>
            <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
              {t('packing.lines.title')}
            </p>
            <div className="space-y-2">
              {lines.map((line, idx) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: rindām nav stabila ID melnraksta stāvoklī
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    aria-label={t('packing.lines.type')}
                    placeholder={t('packing.lines.type')}
                    value={line.packageType}
                    onChange={(e) => setLine(idx, { packageType: e.target.value })}
                  />
                  <Input
                    aria-label={t('packing.lines.count')}
                    placeholder={t('packing.lines.count')}
                    type="number"
                    min="1"
                    className="w-20 shrink-0"
                    value={line.count}
                    onChange={(e) => setLine(idx, { count: e.target.value })}
                  />
                  <Input
                    aria-label={t('packing.lines.net')}
                    placeholder={t('packing.lines.net')}
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-24 shrink-0"
                    value={line.netKg}
                    onChange={(e) => setLine(idx, { netKg: e.target.value })}
                  />
                  <Input
                    aria-label={t('packing.lines.gross')}
                    placeholder={t('packing.lines.gross')}
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-24 shrink-0"
                    value={line.grossKg}
                    onChange={(e) => setLine(idx, { grossKg: e.target.value })}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={t('packing.editor.remove')}
                    onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" aria-hidden />
                  </Button>
                </div>
              ))}
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="mt-2"
              onClick={() => setLines((prev) => [...prev, { ...EMPTY_LINE }])}
            >
              <Plus className="h-4 w-4" aria-hidden />
              {t('packing.editor.addLine')}
            </Button>
          </div>

          <div className="mt-auto flex justify-end gap-2 pt-4">
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {t('packing.editor.cancel')}
            </Button>
            <Button disabled={busy} onClick={() => void save()}>
              {t('packing.editor.save')}
            </Button>
          </div>
        </div>
      </SlideOver>
    </Card>
  );
}
